import { NullUUID, UUID } from "@bitory-gmbh/nidavellir";
import {
  updateVbsConfirmationCache,
  useVbsConfirmationShowQuery,
  useVbsConfirmationPatchMutation,
} from "../api/confirmation-api";
import { useOptimisticDebouncedMutation } from "@/shared/lib/debounce/use-optimistic-debounced-mutation";
import { Card, CardContent } from "@/shared/components/ui/card";
import { ConfirmationBaseForm } from "@/services/backend/documents/components/confirmation-base-form";
import { CpyName } from "@/services/backend/cpy/types/entity-names";
import { GSMServiceKind } from "@/services/backend/services/types/kinds";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { inputValidationErrorMessages } from "@/shared/components/errors/input-error-messages";
import t from "@/lang/lang";
import { FieldSeparator } from "@/shared/components/ui/field";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { Confirmation } from "../types/confirmation";
import {
  ConfirmationBasePatchRequest,
  patchConfirmationBase,
} from "@/services/backend/documents/types/confirmation";

export function ConfirmationDataCard({
  confirmationId,
}: {
  confirmationId: UUID;
}) {
  const { data, error: dataError } = useVbsConfirmationShowQuery({
    id: confirmationId,
  });

  return (
    <div>
      {data && <ConfirmationData data={data} />}
      {dataError && <RTKQueryErrorAlert error={dataError} />}
    </div>
  );
}

function ConfirmationData({ data }: { data: Confirmation }) {
  const { id: confirmationId } = data;
  const [set, { error }] = useOptimisticDebouncedMutation(
    {
      id: data.id,
      date: data.date,
      orderDate: data.orderDate,
      orderReference: data.orderReference,
      offerDate: data.offerDate,
      offerReference: data.offerReference,
    },
    updateVbsConfirmationCache,
    useVbsConfirmationPatchMutation(),
  );

  return (
    <Card>
      <CardContent>
        <div className="grid gap-4">
          <ConfirmationBaseForm
            confirmationId={confirmationId}
            base={data}
            onBasePatch={(patch: ConfirmationBasePatchRequest) =>
              set({ id: data.id, ...patch }, patchConfirmationBase(data, patch))
            }
            error={error}
            cpyDestinationEntityName={CpyName.VBSConfirmation}
            cpyOrderSourceEntityName={CpyName.VBSOrder}
            cpyOfferSourceEntityName={CpyName.VBSOffer}
            cpyOfferSourcesParams={{
              serviceKind: { active: true, values: [GSMServiceKind.VBS] },
              process: {
                active: data.processId ? true : false,
                values: [data.processId ?? NullUUID],
              },
              immutable: { active: true, values: [true] },
              orderBy: [{ field: "updatedAt" }],
            }}
            cpyOrderSourcesParams={{
              serviceKind: { active: true, values: [GSMServiceKind.VBS] },
              process: {
                active: data.processId ? true : false,
                values: [data.processId ?? NullUUID],
              },
              orderBy: [{ field: "updatedAt" }],
            }}
            disabled={data.immutable}
          />
          <FieldSeparator />
          <TextareaField
            label={t("Interner Kommentar")}
            value={data.internalComment}
            onChange={(e) =>
              set(
                { id: data.id, internalComment: e.target.value },
                { ...data, internalComment: e.target.value },
              )
            }
            errors={[...inputValidationErrorMessages(error, "internalComment")]}
            disabled={data.immutable}
          />
        </div>
      </CardContent>
    </Card>
  );
}
